/**
 * Logging a meal.
 *
 * Natural language is the primary path because the alternative — search, pick,
 * set portion, repeat per item — is what stops people logging by the third
 * day. Type the sentence, glance at what it resolved to, tap save.
 *
 * Everything it resolved is shown before saving, with one-tap corrections,
 * because a parser that is confidently wrong is worse than one that shows its
 * work.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Field, Sheet, fmt } from '../ui/bits';
import { parseMeal } from '../domain/foodparse';
import { loggableItems, macrosOf, reresolve, resolveAll, resolveLlmItems, type ResolvedItem } from '../domain/resolve';
import { searchFoods, type CoreFood } from '../domain/foodsearch';
import { total } from '../domain/macros';
import { addMeal, coreFoods, coreFoodsLoading, loadCoreFoods, notify, settings, today } from '../store';
import { parseMealWithLlm } from '../data/llm';
import { lookupBarcode } from '../data/openfoodfacts';
import type { MealSlot } from '../data/schema';
import { nowIso } from '../lib/time';

const SLOTS: MealSlot[] = ['breakfast', 'lunch', 'dinner', 'snack'];

/** Route a meal to a slot by the clock, so the common case needs no tap. */
function slotForNow(d = new Date()): MealSlot {
  const h = d.getHours();
  if (h < 10) return 'breakfast';
  if (h < 15) return 'lunch';
  if (h < 21) return 'dinner';
  return 'snack';
}

export function FoodSheet(props: { onClose: () => void }): JSX.Element {
  const [text, setText] = useState('');
  const [items, setItems] = useState<ResolvedItem[] | null>(null);
  const [slot, setSlot] = useState<MealSlot>(slotForNow());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { void loadCoreFoods(); }, []);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const foods = coreFoods.value;

  const [parsing, setParsing] = useState(false);

  const parse = async () => {
    if (!foods) { notify('Still loading the food database.', true); return; }
    const parsed = parseMeal(text);
    if (!parsed.length) { notify('Nothing to log yet.', true); return; }
    // Local first: instant, and the answer if the model is off or fails.
    const local = resolveAll(parsed, foods);
    setItems(local);

    if (settings.value.llm.enabled) {
      setParsing(true);
      try {
        const llmItems = await parseMealWithLlm(text, settings.value.llm);
        if (llmItems.length) setItems(resolveLlmItems(llmItems, foods));
      } catch (err) {
        notify(`Assist unavailable: ${err instanceof Error ? err.message : 'failed'}. Using on-device parse.`, true);
      } finally {
        setParsing(false);
      }
    }
  };

  const totals = useMemo(
    () => total((items ?? []).filter((i) => i.macros).map((i) => ({ macros: i.macros! }))),
    [items],
  );

  const save = async () => {
    if (!items) return;
    const loggable = loggableItems(items);
    if (!loggable.length) { notify('Nothing resolved yet.', true); return; }
    setBusy(true);
    try {
      await addMeal({ day: today.value, at: nowIso(), slot, items: loggable, rawText: text });
      notify(`Logged ${loggable.length} ${loggable.length === 1 ? 'item' : 'items'}`);
      props.onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not save', true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title="Log food"
      onClose={props.onClose}
      footer={
        items ? (
          <div class="row" style={{ gap: '10px' }}>
            <button class="btn grow" onClick={() => setItems(null)}>Back</button>
            <button class="btn btn-primary grow" disabled={busy || parsing} onClick={save}>
              {busy ? 'Saving' : parsing ? 'Checking with model…' : `Log ${fmt(totals.kcal)} kcal`}
            </button>
          </div>
        ) : (
          <button class="btn btn-primary btn-full" onClick={() => void parse()} disabled={!text.trim() || coreFoodsLoading.value}>
            {coreFoodsLoading.value ? 'Loading foods' : 'Read it'}
          </button>
        )
      }
    >
      {!items ? (
        <>
          <Field label="What did you eat?" hint="Plain language. Amounts are optional.">
            <input
              ref={inputRef}
              class="input"
              style={{ fontFamily: 'var(--sans)' }}
              placeholder="chicken breast, rice, black coffee"
              value={text}
              enterkeyhint="go"
              onInput={(e) => setText((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void parse(); }}
            />
          </Field>

          <div class="row wrap" style={{ gap: '6px' }}>
            {SLOTS.map((s) => (
              <button key={s} class="chip" aria-pressed={slot === s} onClick={() => setSlot(s)}>
                {s}
              </button>
            ))}
          </div>

          <BarcodeRow
            onFound={(food) => {
              setText((t) => (t ? `${t}, ${food.n}` : food.n));
              if (foods) setItems(resolveAll(parseMeal(food.n), [...foods, food]));
            }}
          />
        </>
      ) : (
        <>
          <div class="list card" style={{ padding: 0 }}>
            {items.map((item, idx) => (
              <ResolvedRow
                key={idx}
                item={item}
                open={editing === idx}
                onToggle={() => setEditing(editing === idx ? null : idx)}
                onChange={(next) => setItems(items.map((x, i) => (i === idx ? next : x)))}
                onRemove={() => {
                  setItems(items.filter((_, i) => i !== idx));
                  setEditing(null);
                }}
              />
            ))}
          </div>
          <div class="card">
            <div class="row-between">
              <span class="label">Total</span>
              <span class="readout readout-md">{fmt(totals.kcal)}<span class="unit"> kcal</span></span>
            </div>
            <div class="row" style={{ gap: '14px', marginTop: '8px' }}>
              <span class="unit">P {fmt(totals.protein)}g</span>
              <span class="unit">C {fmt(totals.carbs)}g</span>
              <span class="unit">F {fmt(totals.fat)}g</span>
            </div>
          </div>
        </>
      )}
    </Sheet>
  );
}

/** One resolved line, with its correction affordances behind a tap. */
function ResolvedRow(props: {
  item: ResolvedItem;
  open: boolean;
  onToggle: () => void;
  onChange: (next: ResolvedItem) => void;
  onRemove: () => void;
}): JSX.Element {
  const { item } = props;
  const foods = coreFoods.value ?? [];
  const [query, setQuery] = useState('');

  const options = query.trim()
    ? searchFoods(foods, query, 8).map((m) => m.food)
    : item.alternatives;

  const amountLabel = item.grams !== null
    ? [
        fmt(item.parsed.qty, item.parsed.qty % 1 === 0 ? 0 : 2),
        item.parsed.unit ?? '',
        `· ${fmt(item.grams)} g`,
      ].filter(Boolean).join(' ')
    : 'amount needed';

  return (
    <div style={{ borderBottom: '1px solid var(--line)' }}>
      <button class="item" style={{ borderBottom: 'none' }} onClick={props.onToggle}>
        <div class="col grow">
          <span class="item-title">{item.food?.n ?? item.parsed.query}</span>
          <span class="item-sub">
            {item.issue === 'no-match' ? 'No match — tap to search'
              : item.issue === 'no-amount' ? 'Tap to set an amount'
              : amountLabel}
            {item.parsed.assumedQty && !item.issue ? ' · assumed 1' : ''}
          </span>
        </div>
        {item.macros ? (
          <span class="readout readout-sm">{fmt(item.macros.kcal)}<span class="unit"> kcal</span></span>
        ) : (
          <span class="badge is-warn">Check</span>
        )}
      </button>

      {props.open ? (
        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div class="row" style={{ gap: '8px' }}>
            <input
              class="input"
              inputMode="decimal"
              style={{ width: '90px' }}
              value={String(item.parsed.qty)}
              aria-label="Amount"
              onInput={(e) => {
                const qty = Number((e.target as HTMLInputElement).value);
                if (Number.isFinite(qty) && item.food) props.onChange(reresolve(item, item.food, qty, item.parsed.unit));
              }}
            />
            <select
              class="select grow"
              value={item.parsed.unit ?? ''}
              aria-label="Unit"
              onChange={(e) => {
                const unit = (e.target as HTMLSelectElement).value || null;
                if (item.food) props.onChange(reresolve(item, item.food, item.parsed.qty, unit));
              }}
            >
              <option value="g">grams</option>
              <option value="oz">ounces</option>
              {(item.food?.po ?? []).map((p) => (
                <option key={p[0]} value={p[0]}>{p[0]} ({fmt(p[1])} g)</option>
              ))}
              <option value="">each</option>
            </select>
          </div>

          <input
            class="input"
            style={{ fontFamily: 'var(--sans)' }}
            placeholder="Search for a different food"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          />
          {options.length ? (
            <div class="list" style={{ maxHeight: '180px', overflowY: 'auto' }}>
              {options.map((f) => (
                <button
                  key={f.i}
                  class="item"
                  onClick={() => {
                    props.onChange(reresolve({ ...item, alternatives: options.filter((o) => o.i !== f.i) }, f, item.parsed.qty, item.parsed.unit));
                    setQuery('');
                  }}
                >
                  <div class="col grow">
                    <span class="item-title" style={{ fontSize: '14px' }}>{f.n}</span>
                    <span class="item-sub">{fmt(macrosOf(f).kcal)} kcal / 100 g</span>
                  </div>
                </button>
              ))}
            </div>
          ) : null}

          <button class="btn btn-sm" onClick={props.onRemove}>Remove this item</button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Barcode entry.
 *
 * Typed entry always works; the camera is offered on top of it. On iOS a
 * home-screen PWA re-asks for camera permission on every cold launch and the
 * Barcode Detection API is still behind a flag, so the scanner is a lazily
 * loaded decoder over getUserMedia, and typing the number stays a first-class
 * path rather than a consolation.
 */
function BarcodeRow(props: { onFound: (food: CoreFood) => void }): JSX.Element {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const stopRef = useRef<(() => void) | null>(null);

  const lookup = async (value: string) => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      const item = await lookupBarcode(value);
      if (!item) { notify('No nutrition data for that barcode.', true); return; }
      props.onFound({
        i: item.id, n: [item.brand, item.name].filter(Boolean).join(' '),
        k: item.macros.kcal, p: item.macros.protein, c: item.macros.carbs, f: item.macros.fat,
        ...(item.macros.fiber !== undefined ? { fi: item.macros.fiber } : {}),
        po: item.portions?.[0]?.grams ? [['serving', item.portions[0].grams]] : [['g', 1]],
        q: item.name.toLowerCase().split(/\s+/),
        r: 0,
      });
      setCode('');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Lookup failed', true);
    } finally {
      setBusy(false);
    }
  };

  const startScan = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      setScanning(true);
      const video = videoRef.current;
      if (video) { video.srcObject = stream; await video.play(); }

      const { readBarcodesFromImageData, prepareZXingModule } = await import('zxing-wasm/reader');
      await prepareZXingModule({ fireImmediately: true });

      let stop = false;
      stopRef.current = () => {
        stop = true;
        stream.getTracks().forEach((t) => t.stop());
        setScanning(false);
      };

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const tick = async () => {
        if (stop || !video || !ctx) return;
        if (video.videoWidth) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);
          const results = await readBarcodesFromImageData(
            ctx.getImageData(0, 0, canvas.width, canvas.height),
            { formats: ['EAN-13', 'EAN-8', 'UPC-A', 'UPC-E'] },
          );
          if (results.length && results[0]?.text) {
            stopRef.current?.();
            await lookup(results[0].text);
            return;
          }
        }
        setTimeout(() => void tick(), 220);
      };
      void tick();
    } catch {
      notify('Camera unavailable. Type the number instead.', true);
      setScanning(false);
    }
  };

  useEffect(() => () => stopRef.current?.(), []);

  return (
    <div class="stack">
      <div class="row" style={{ gap: '8px' }}>
        <input
          class="input grow"
          inputMode="numeric"
          placeholder="Barcode"
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
        />
        <button class="btn" disabled={busy || !code.trim()} onClick={() => void lookup(code)}>
          {busy ? '…' : 'Find'}
        </button>
        <button class="btn" onClick={() => (scanning ? stopRef.current?.() : void startScan())}>
          {scanning ? 'Stop' : 'Scan'}
        </button>
      </div>
      {scanning ? (
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ width: '100%', borderRadius: 'var(--r-sm)', border: '1px solid var(--line-lit)', maxHeight: '220px', objectFit: 'cover' }}
        />
      ) : null}
    </div>
  );
}
